import React, { useCallback, useState } from 'react';
import { SetupLayout } from '../../components/layout/SetupLayout';
import { useFetchResources, useFetchOrganizations } from '../../hooks/api';
import { Tooltip } from '../../components/Tooltip';
import { useAuth } from '../../hooks/auth';
import { useResourceSearch } from '../../hooks/ui';
import { useNavigate, useLocation } from 'react-router-dom';
import { logger } from '../../utils/logger';
import {
  useSnapshotName,
  useModalState,
  useOutsideClick,
  useMigrationSetupGuidanceSeen
} from '../../hooks/ui';
import { useResourceFilters, useResourceSelection } from '../../hooks/resource';
import generateDefaultSnapshotName from '../../utils/snapshot';
import { Modal } from '../../components/Modals/Shared/Modal';
import { RepliCloudFAQsModal } from '../../components/Modals/FAQ/RepliCloudFAQsModal';
import OrganizationSelector from '../../components/organization/OrganizationSelector';
import ResourceSelector from '../../components/resource/ResourceSelector';
import SelectedResourcesTableHeader from '../../components/resource/table/SelectedResourcesTableHeader';
import SelectedResourcesTableBody from '../../components/resource/table/SelectedResourcesTableBody';
import SelectedResourcesTableButtons from '../../components/resource/table/SelectedResourcesTableButtons';
import createMigration from '../../services/Migration/createMigrationService';
import updateMigration from '../../services/Migration/updateMigrationService';
import { MigrationStatusEnum as MigrationStatus } from '../../types';
import { mapResourceTypeToApiPath, mapApiTypeToDisplayName } from '../../utils/resourceTypeMapper';
import { RESOURCE_TYPES } from '../../utils/resourceTypes';
import { clearOrgCache } from '../../utils/resourceCache';
import { formatRegion } from '../../utils/formatters';
import { ui } from '../../config/uiSizes';

const MigrationSetup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo, accessToken } = useAuth();
  const [migrationName, setMigrationName] = React.useState('');
  const { snapshotName, setSnapshotName, setSnapshotNameEdited } = useSnapshotName(migrationName);
  const [sourceOrganization, setSourceOrganization] = React.useState('');
  const [targetOrganization, setTargetOrganization] = React.useState('');
  const { organizations, fetchOrganizations } = useFetchOrganizations();
  const {
    resources,
    fetchedResourcesByType: _fetchedResourcesByType, // Dictionary of resources by type (available for future UI enhancements)
    loading: loadingResources,
    error: resourceFetchError,
    fetchProgress,
    fetchMultipleResourceTypes,
    clearResources,
    typeLoadOrder,
    failedTypes,
    removeFailedTypes
  } = useFetchResources();
  const [resourceType, setResourceType] = React.useState('');
  const [selectedResourceTypes, setSelectedResourceTypes] = React.useState<string[]>([]);
  const [resourceSearch, setResourceSearch] = React.useState('');
  const [resourcesDropdownOpen, setResourcesDropdownOpen] = React.useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = React.useState(false);
  const [confirmScope, setConfirmScope] = React.useState<'all' | 'current'>('all');
  const [validationError, setValidationError] = React.useState<string[]>([]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = React.useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmLoading, setConfirmLoading] = React.useState(false);
  const [isFullSnapshotMode, setIsFullSnapshotMode] = React.useState(false);
  const [isPrePopulatedForm, setIsPrePopulatedForm] = React.useState(false);
  const [prePopulatedFormId, setPrePopulatedFormId] = React.useState('');
  // Tracks whether the page is loading data for the Edit Migration Settings prefill flow.
  // True from the moment migrationData is detected until resource selections are applied.
  const [isEditMigrationPrefillLoading, setIsEditMigrationPrefillLoading] = React.useState(false);
  const [typeSearch, setTypeSearch] = React.useState('');
  const [nameSearch, setNameSearch] = React.useState('');
  const [includeDependencies, setIncludeDependencies] = React.useState(true);
  const { modalState, handleRunMigrationClick, handleModalClose } = useModalState();
  const [showFAQs, setShowFAQs] = useState(false);
  // Per-user first-time guidance tracking.
  // hasSeen = read from localStorage on mount (determines this session's initial state).
  // showGuidanceBanner = local session state, starts true if this is their first visit.
  const { hasSeen: guidanceHasSeen, markAsSeen: markGuidanceAsSeen } =
    useMigrationSetupGuidanceSeen(userInfo?.userEmail ?? null);
  const [showGuidanceBanner, setShowGuidanceBanner] = useState(!guidanceHasSeen);

  const getOrganizationKey = useCallback((org: { name: string; shortName?: string }): string => {
    const shortName = org.shortName?.trim();
    return shortName && shortName.length > 0 ? shortName : org.name;
  }, []);

  const findOrganization = useCallback(
    (value: string) =>
      organizations.find((org) => getOrganizationKey(org) === value || org.name === value),
    [organizations, getOrganizationKey]
  );

  // Log page mount for traceability
  React.useEffect(() => {
    logger.info('Page: MigrationSetup mounted');
  }, []);

  // On first visit, immediately write to localStorage so next login it won't show again.
  // The banner still stays visible this session until the user clicks X.
  React.useEffect(() => {
    if (!guidanceHasSeen) {
      markGuidanceAsSeen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);
  const dropdownButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const selectedResourceTitle = 'Selected Resources to Migrate';
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const { searchedResources } = useResourceFilters(
    resources,
    selectedResourceTypes,
    resourceSearch,
    typeLoadOrder,
    failedTypes
  );
  const {
    selectedResourceIds,
    setSelectedResourceIds,
    handleResourceToggle,
    handleSelectAllToggle,
    handleResetSelections,
    allSelectedResources,
    resourceTypeCounts
  } = useResourceSelection(resources, resourceType);

  // Pre-populate form from failed migration data
  React.useEffect(() => {
    const migrationData = (
      location.state as {
        migrationData?: {
          name: string;
          sourceOrgName: string;
          targetOrgName: string;
          sourceOrgId: string;
          targetOrgId: string;
          resources: string[];
          id: string;
          isPrePopulatedForm: boolean;
        };
      }
    )?.migrationData;

    if (migrationData) {
      setIsEditMigrationPrefillLoading(true);
      // Set basic fields
      setMigrationName(migrationData.name);
      setSnapshotName(migrationData.name);
      setSourceOrganization(migrationData.sourceOrgName);
      setTargetOrganization(migrationData.targetOrgName);
      setIsPrePopulatedForm(true);
      setPrePopulatedFormId(migrationData.id);

      // Parse resources from "type::id" format
      const resourcesByType: Record<string, string[]> = {};
      const resourceTypes = new Set<string>();

      migrationData.resources.forEach((resource) => {
        const [apiType, id] = resource.split('::');

        // Skip if apiType or id is missing
        if (!apiType || !id) return;

        // Convert API type to display name (e.g., 'architect_datatable' -> 'Architect Datatable')
        const displayType = mapApiTypeToDisplayName(apiType);

        resourceTypes.add(displayType);
        if (!resourcesByType[displayType]) {
          resourcesByType[displayType] = [];
        }
        resourcesByType[displayType].push(id);
      });

      // Set selected resource types
      const typesToFetch = Array.from(resourceTypes);
      setSelectedResourceTypes(typesToFetch);

      // Store resource selection data to apply after resources are fetched
      // This will be used by a separate effect that watches for resources to load
      sessionStorage.setItem('pendingResourceSelection', JSON.stringify(resourcesByType));

      // Automatically fetch resources for pre-populated migration.
      // Use migrationData.sourceOrgName directly to avoid setState timing issues.
      if (typesToFetch.length > 0 && migrationData.sourceOrgName) {
        fetchMultipleResourceTypes(typesToFetch, migrationData.sourceOrgName);
        prevFetchedTypesRef.current = typesToFetch;
      } else {
        // No resources to fetch; field population is already complete
        setIsEditMigrationPrefillLoading(false);
      }

      // Clear location state so refresh doesn't re-populate
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate, setSnapshotName, fetchMultipleResourceTypes]);

  // Apply resource selection after resources are fetched
  React.useEffect(() => {
    const pendingSelection = sessionStorage.getItem('pendingResourceSelection');

    if (pendingSelection && resources.length > 0) {
      try {
        const resourcesByType: Record<string, string[]> = JSON.parse(pendingSelection);

        // Find and select matching resources
        const idsToSelect: string[] = [];
        resources.forEach((resource) => {
          const expectedIds = resourcesByType[resource.type];
          if (expectedIds) {
            const isMatch =
              expectedIds.includes(resource.id) ||
              expectedIds.includes('*') ||
              expectedIds.includes('**');
            if (isMatch) {
              idsToSelect.push(resource.id);
            }
          }
        });

        if (idsToSelect.length > 0) {
          setSelectedResourceIds(idsToSelect);
          sessionStorage.removeItem('pendingResourceSelection');
        } else {
          // Resources loaded but none matched; prefill is as complete as it can be
          sessionStorage.removeItem('pendingResourceSelection');
        }
        setIsEditMigrationPrefillLoading(false);
      } catch (err) {
        logger.error('Failed to parse pending resource selection', { error: err });
        sessionStorage.removeItem('pendingResourceSelection');
        setIsEditMigrationPrefillLoading(false);
      }
    }
  }, [resources, setSelectedResourceIds]);

  // Remove selected resource IDs when their types are unchecked
  // But preserve selections when new types are added
  const prevSelectedTypesRef = React.useRef<string[]>([]);
  React.useEffect(() => {
    const prevTypes = prevSelectedTypesRef.current;

    if (selectedResourceTypes.length === 0 && prevTypes.length > 0) {
      // If no types selected anymore, clear all selections
      setSelectedResourceIds([]);
    } else if (prevTypes.length > selectedResourceTypes.length) {
      // Types were removed - filter out resource IDs belonging to removed types
      const removedTypes = prevTypes.filter((t) => !selectedResourceTypes.includes(t));
      if (removedTypes.length > 0) {
        // Use allSelectedResources to check types, not just current resources array
        setSelectedResourceIds((prevIds) => {
          return prevIds.filter((id) => {
            const resource = allSelectedResources.find((r) => r.id === id);
            return resource && !removedTypes.includes(resource.type);
          });
        });
      }
    }

    // When new types are added (length increases), selections are preserved automatically
    prevSelectedTypesRef.current = selectedResourceTypes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResourceTypes]);

  // Fetch organizations on mount
  React.useEffect(() => {
    if (accessToken) {
      fetchOrganizations();
    }
  }, [accessToken, fetchOrganizations]);

  // Clear cache when source organization changes to get fresh data
  const prevSourceOrgRef = React.useRef<string>('');
  React.useEffect(() => {
    if (
      sourceOrganization &&
      prevSourceOrgRef.current &&
      prevSourceOrgRef.current !== sourceOrganization
    ) {
      logger.info('Source organization changed in MigrationSetup', {
        old: prevSourceOrgRef.current,
        new: sourceOrganization,
        role: 'source'
      });
      clearOrgCache(prevSourceOrgRef.current);
    }
    prevSourceOrgRef.current = sourceOrganization;
  }, [sourceOrganization]);

  // Log target organization changes
  const prevTargetOrgRef = React.useRef<string>('');
  React.useEffect(() => {
    if (
      targetOrganization &&
      prevTargetOrgRef.current &&
      prevTargetOrgRef.current !== targetOrganization
    ) {
      logger.info('Target organization changed in MigrationSetup', {
        old: prevTargetOrgRef.current,
        new: targetOrganization,
        role: 'target'
      });
    }
    prevTargetOrgRef.current = targetOrganization;
  }, [targetOrganization]);

  // Log resource type changes
  const prevResourceTypeRef = React.useRef<string>('');
  React.useEffect(() => {
    if (
      resourceType &&
      prevResourceTypeRef.current &&
      prevResourceTypeRef.current !== resourceType
    ) {
      logger.info('Resource type changed in MigrationSetup', {
        from: prevResourceTypeRef.current,
        to: resourceType,
        selectedCount: selectedResourceIds.length
      });
    }
    prevResourceTypeRef.current = resourceType;
  }, [resourceType, selectedResourceIds.length]);

  // When full snapshot mode is active, display all resource types in the table
  const displayedResources = React.useMemo(() => {
    if (isFullSnapshotMode) {
      return RESOURCE_TYPES.map((type) => ({
        id: `all-${type}`,
        name: '* (All)',
        type: type
      }));
    }
    return allSelectedResources;
  }, [isFullSnapshotMode, allSelectedResources]);

  // Track fetched types to determine if we have pending unfetched types
  const prevFetchedTypesRef = React.useRef<string[]>([]);

  // Determine if there are pending types that haven't been fetched
  const hasPendingTypes = React.useMemo(() => {
    if (selectedResourceTypes.length === 0) return false;
    const prevFetched = prevFetchedTypesRef.current;
    const newTypes = selectedResourceTypes.filter((t) => !prevFetched.includes(t));
    return newTypes.length > 0;
  }, [selectedResourceTypes]);

  // Manual fetch handler
  const handleFetchResources = React.useCallback(() => {
    if (!sourceOrganization || !accessToken) return;

    const prevFetched = prevFetchedTypesRef.current;
    const newTypes = selectedResourceTypes.filter((t) => !prevFetched.includes(t));

    if (newTypes.length > 0) {
      fetchMultipleResourceTypes(newTypes, sourceOrganization);
      prevFetchedTypesRef.current = selectedResourceTypes;
    }
  }, [sourceOrganization, selectedResourceTypes, accessToken, fetchMultipleResourceTypes]);

  // Clear resources when types are removed
  React.useEffect(() => {
    const prevFetched = prevFetchedTypesRef.current;

    if (selectedResourceTypes.length === 0 && prevFetched.length > 0) {
      clearResources();
      prevFetchedTypesRef.current = [];
    } else if (prevFetched.length > selectedResourceTypes.length) {
      // Types were removed - update the ref to only include currently selected types
      const removedTypes = prevFetched.filter((t) => !selectedResourceTypes.includes(t));
      prevFetchedTypesRef.current = prevFetched.filter((t) => selectedResourceTypes.includes(t));
      // Remove failed types for types that were deselected
      if (removedTypes.length > 0) {
        removeFailedTypes(removedTypes);
      }
    }
    // Note: We don't automatically fetch when types are added - user must click "Load Resources"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResourceTypes]);

  // Close dropdown when resourceType changes (so UI resets when switching)
  React.useEffect(() => setResourcesDropdownOpen(false), [resourceType]);

  // Close dropdown when resources start loading
  React.useEffect(() => {
    if (loadingResources) {
      setResourcesDropdownOpen(false);
    }
  }, [loadingResources]);

  // Close dropdown when clicking outside
  useOutsideClick([dropdownButtonRef, panelRef], () => setResourcesDropdownOpen(false));

  // Clear search when dropdown closes
  React.useEffect(() => {
    if (!resourcesDropdownOpen) setResourceSearch('');
  }, [resourcesDropdownOpen]);

  const openConfirmModal = React.useCallback((scope: 'all' | 'current') => {
    setConfirmScope(scope);
    setConfirmModalOpen(true);
  }, []);

  const totalSelectedCount = displayedResources.length;

  const isFormValid = React.useMemo(() => {
    const basicValid = !!(
      migrationName.trim() &&
      migrationName.trim().length >= 2 &&
      sourceOrganization &&
      targetOrganization &&
      (totalSelectedCount > 0 || isFullSnapshotMode)
    );

    if (!basicValid) return false;

    return true;
  }, [
    migrationName,
    sourceOrganization,
    targetOrganization,
    totalSelectedCount,
    isFullSnapshotMode
  ]);

  const isButtonEnabled = isFormValid;

  const validateForm = useCallback(
    (
      name: string,
      sourceOrg: string,
      targetOrg: string,
      count: number,
      fullSnapshot: boolean
    ): boolean => {
      const errors: string[] = [];
      if (!name.trim()) {
        errors.push('Please enter a Migration Name');
      }
      if (name.trim().length < 2) {
        errors.push('Migration Name must be at least 2 characters');
      }
      if (!sourceOrg) {
        errors.push('Please select a Source Organization');
      }
      if (!targetOrg) {
        errors.push('Please select a Target Organization');
      }
      if (count === 0 && !fullSnapshot) {
        errors.push('Please select at least one resource to migrate or use Full Export option');
      }
      setValidationError(errors);
      if (errors.length > 0) {
        logger.warn('Migration form validation failed', {
          errors,
          formState: { name, sourceOrg, targetOrg, resourceCount: count, fullSnapshot }
        });
      }
      return errors.length === 0;
    },
    []
  );

  React.useEffect(() => {
    if (validationError.length > 0 && isFormValid) {
      setValidationError([]);
    }
  }, [
    allSelectedResources.length,
    hasAttemptedSubmit,
    totalSelectedCount,
    isFullSnapshotMode,
    validationError,
    isFormValid
  ]);

  React.useEffect(() => {
    if (hasAttemptedSubmit) {
      validateForm(
        migrationName,
        sourceOrganization,
        targetOrganization,
        totalSelectedCount,
        isFullSnapshotMode
      );
    }
  }, [
    migrationName,
    sourceOrganization,
    targetOrganization,
    totalSelectedCount,
    isFullSnapshotMode,
    hasAttemptedSubmit,
    validateForm
  ]);

  const { filteredResourcesByType } = useResourceSearch(
    allSelectedResources,
    typeSearch,
    nameSearch
  );

  // Filter resource types when in full snapshot mode
  const filteredResourceTypes = React.useMemo(() => {
    if (!typeSearch.trim()) return RESOURCE_TYPES;
    const searchLower = typeSearch.trim().toLowerCase();
    return RESOURCE_TYPES.filter((type) => type.toLowerCase().includes(searchLower));
  }, [typeSearch]);

  const tableScrollClass = `h-72 overflow-y-auto p-3`;

  // Handle selecting all resource types with wildcard
  const handleSelectAllResourceTypes = React.useCallback(() => {
    if (!sourceOrganization) {
      setValidationError(['Please select a Source Organization first']);
      return;
    }

    // Clear any manual selections and enable full snapshot mode
    handleResetSelections('all');
    setIsFullSnapshotMode(true);

    logger.info('Enabled full Export mode - all resource types will be included', {
      count: RESOURCE_TYPES.length
    });
  }, [sourceOrganization, handleResetSelections, setValidationError]);

  // Calculate total counts for each resource type for display in the table header badges
  const totalCountForType = RESOURCE_TYPES.map((displayType) => {
    const totalCountForType = resources.filter((r) => r.type === displayType).length;
    return { displayType, totalCountForType };
  });

  const totalCountForAllTypes =
    totalCountForType.length > 0
      ? new Map(
          totalCountForType.map(({ displayType, totalCountForType }) => [
            displayType,
            totalCountForType
          ])
        )
      : resourceTypeCounts;

  // Disable full snapshot mode when user manually selects resources
  React.useEffect(() => {
    if (allSelectedResources.length > 0 && isFullSnapshotMode) {
      setIsFullSnapshotMode(false);
    }
  }, [allSelectedResources.length, isFullSnapshotMode]);

  //run confirm migration logic here
  const handleRunConfirm = async () => {
    setConfirmLoading(true);
    setError(null);

    try {
      if (!accessToken || !userInfo?.orgId) {
        throw new Error('Missing authentication information');
      }

      // Find org IDs from selected org names
      const sourceOrg = findOrganization(sourceOrganization);
      const targetOrg = findOrganization(targetOrganization);

      if (!sourceOrg || !targetOrg) {
        throw new Error('Could not find organization IDs');
      }

      let resourceStrings: string[] = [];

      if (isFullSnapshotMode) {
        // Full snapshot mode: send empty array to indicate no filtering (includes all current and future resources)
        resourceStrings = [];
      } else {
        // Manual selection mode: use existing logic
        // Group selected resources by type to detect if all resources of a type are selected
        const resourcesByType = new Map<string, typeof allSelectedResources>();
        allSelectedResources.forEach((resource) => {
          const existing = resourcesByType.get(resource.type) || [];
          resourcesByType.set(resource.type, [...existing, resource]);
        });

        // Build resources array: use "type::*" if all resources of that type are selected,
        // otherwise use individual "type::name" entries
        resourcesByType.forEach((selectedOfType, displayType) => {
          const apiType = mapResourceTypeToApiPath(displayType);

          // Check if all resources of this type are selected using tracked counts
          const isAllSelectedResources =
            totalCountForAllTypes !== undefined &&
            totalCountForAllTypes.get(displayType) !== undefined &&
            allSelectedResources.filter((r) => r.type === displayType).length ===
              totalCountForAllTypes.get(displayType);

          if (isAllSelectedResources) {
            // All resources of this type selected - use wildcard
            resourceStrings.push(`${apiType}::*`);
          } else {
            // Partial selection - add individual resources
            selectedOfType.forEach((resource) => {
              resourceStrings.push(`${apiType}::${resource.id}`);
            });
          }
        });
      }

      if (isPrePopulatedForm) {
        logger.info('Creating export from pre-populated form', {
          snapshotName,
          sourceOrgId: sourceOrg.id,
          sourceOrgName: sourceOrg.name,
          resourceCount: resourceStrings.length
        });

        // Update the existing snapshot request (using same endpoint as migrations, but without target org)
        const response = await updateMigration(accessToken, prePopulatedFormId, {
          name: snapshotName,
          sourceOrgId: sourceOrg.id,
          sourceOrgName: getOrganizationKey(sourceOrg),
          resources: resourceStrings,
          advanceToNextStatus: false,
          status: MigrationStatus.ExportQueued // Set status back to queued so it can be picked up by the scheduler again
        });

        logger.info('Export updated successfully from pre-populated form', {
          migrationId: response.id,
          status: response.status
        });
      } else {
        // Create the migration request
        const response = await createMigration(accessToken, {
          name: migrationName,
          customerId: userInfo.orgId,
          createdBy: userInfo.userEmail || userInfo.userName,
          sourceOrgId: sourceOrg.id,
          sourceOrgName: getOrganizationKey(sourceOrg),
          targetOrgId: targetOrg.id,
          targetOrgName: getOrganizationKey(targetOrg),
          resources: resourceStrings,
          exportAll: isFullSnapshotMode,
          includeDependencies
        });

        logger.info('Migration created successfully', {
          migrationId: response.id,
          status: response.status
        });
      }

      handleModalClose();

      // Navigate to dashboard
      navigate('/landing');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create migration';
      logger.error('Failed to create migration', { error: errorMsg });
      console.error('Migration creation failed', err);
      setError(
        'An error occurred while creating the migration. Please try again. \nError: ' + errorMsg
      );
    } finally {
      setConfirmLoading(false);
    }
  };

  const leftContent = (
    <>
      <div>
        <div className="space-y-6">
          <div className={`flex flex-col sm:flex-row items-center ${ui.gap.md} mt-4`}>
            <label
              htmlFor="migrationName"
              className={`w-48 ${ui.text.formSectionLabel} font-semibold text-gray-800 dark:text-gray-100`}
            >
              Migration Name:
            </label>
            <div className="flex-1 w-full">
              <input
                id="migrationName"
                type="text"
                value={migrationName}
                placeholder="Enter Migration Name"
                onChange={(e) => {
                  setMigrationName(e.target.value);
                }}
                className={`w-full ${ui.input.custom} border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  migrationName.trim() && migrationName.trim().length < 2
                    ? 'border-red-500 dark:border-red-600'
                    : 'border-gray-300 dark:border-gray-700'
                }`}
              />
              {migrationName.trim() && migrationName.trim().length < 2 && (
                <p className={`mt-1 ${ui.text.xs} text-red-600 dark:text-red-400`}>
                  Migration Name must be at least 2 characters long
                </p>
              )}
            </div>
          </div>

          <div className={`flex flex-col sm:flex-row items-center ${ui.gap.md}`}>
            <OrganizationSelector
              orgVariant="sourceOrganization"
              value={sourceOrganization}
              onChange={(org) => {
                setSourceOrganization(org);
              }}
            />
          </div>
          <div className={`flex flex-col sm:flex-row items-center ${ui.gap.md}`}>
            <OrganizationSelector
              orgVariant="targetOrganization"
              value={targetOrganization}
              onChange={(org) => {
                setTargetOrganization(org);
              }}
            />
          </div>
          <p
            className={`${ui.text.formSectionLabel} font-semibold text-gray-800 dark:text-gray-100 !mt-7`}
          >
            Created by:{' '}
            <span className="text-blue-700 dark:text-blue-500">{userInfo?.userName}</span>
          </p>
        </div>
      </div>

      <div>
        <h3
          className={`pt-6 border-t-2 border-gray-300 dark:border-gray-600 ${ui.text.formStepTitle} text-gray-800 dark:text-gray-100 ${ui.mb.section}`}
        >
          <span className="font-extrabold">Pre-Migration Export</span>
          <Tooltip
            content={
              <span className="font-semibold">
                A name to identify the exported data from your source organization before the
                migration begins.
              </span>
            }
            position="right"
          />
        </h3>
        <div className={`flex flex-col sm:flex-row items-start ${ui.gap.sm}`}>
          <label
            htmlFor="snapshotName"
            className={`w-48 ${ui.text.formSectionLabel} font-semibold text-gray-800 dark:text-gray-100`}
          >
            Export Name:
          </label>

          <div className="flex-1">
            <div className="flex items-center gap-2">
              <input
                id="snapshotName"
                value={snapshotName}
                onChange={(e) => {
                  setSnapshotName(e.target.value);
                  setSnapshotNameEdited(true);
                }}
                className={`flex-1 ${ui.input.custom} border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                aria-label="Export name"
              />
              <button
                type="button"
                className="text-sm text-blue-600 dark:text-blue-500 hover:underline"
                onClick={() => {
                  const defaultName = generateDefaultSnapshotName(migrationName);
                  setSnapshotName(defaultName);
                  setSnapshotNameEdited(false);
                }}
                title="Reset to auto-generated snapshot name"
              >
                Reset
              </button>
            </div>

            <p className={`${ui.text.xs} text-gray-500 mt-2`}>
              Auto-filled from Migration Name — but user editable.
            </p>
          </div>
        </div>
      </div>

      <div>
        <div
          className={`flex lg:flex-col xl:flex-row items-center lg:items-start xl:items-center justify-between lg:gap-2 xl:gap-0 pt-6 border-t-2 border-gray-300 dark:border-gray-600 ${ui.mb.section}`}
        >
          <div className={`flex items-center ${ui.gap.sm}`}>
            <h3 className="text-lg md:text-xl font-extrabold text-gray-800 dark:text-gray-100">
              Select Resources to Migrate
            </h3>
            <Tooltip
              content={
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                      Manual Selection
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">
                      Select one or more resource types, then click "Load Resources" to fetch
                      resources. Then pick individual resources from the resources dropdown or
                      select all for each type. You can add or remove types and resources at any
                      time—your selections will be saved and seen in the table to the right. All
                      dependent Genesys objects tied to the selected resources will be automatically
                      included in the migration and may be excluded during the transformation step.
                    </p>
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-600 pt-2">
                    <p className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                      Full Export
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">
                      Click "Select All Resources (Full Export)" to automatically include all
                      resource types with wildcard selection (*) - no need to pick individual types
                      or resources.
                    </p>
                  </div>
                </div>
              }
              position="right"
              className={ui.text.formSectionLabel}
            />
          </div>
          <button
            type="button"
            onClick={
              isFullSnapshotMode ? () => setIsFullSnapshotMode(false) : handleSelectAllResourceTypes
            }
            disabled={!sourceOrganization}
            className={`whitespace-nowrap shrink-0 lg:self-end xl:self-auto ${ui.button.md} rounded-lg font-semibold shadow transition ${
              sourceOrganization
                ? isFullSnapshotMode
                  ? 'bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-600 cursor-pointer'
                  : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white cursor-pointer'
                : 'bg-gray-400 dark:bg-gray-600 text-gray-200 cursor-not-allowed opacity-60'
            }`}
            title={
              !sourceOrganization
                ? 'Please select a Source Organization first'
                : 'Select all resource types for full export'
            }
          >
            {isFullSnapshotMode ? (
              <>
                <span className="lg:hidden">Exit Full Export</span>
                <span className="hidden lg:inline">Exit Full Export</span>
              </>
            ) : (
              <>
                <span className="lg:hidden">Select All Resources (Full Export)</span>
                <span className="hidden lg:inline">Full Export</span>
              </>
            )}
          </button>
        </div>

        <ResourceSelector
          resourceType={resourceType}
          onResourceTypeChange={setResourceType}
          selectedResourceTypes={selectedResourceTypes}
          onSelectedResourceTypesChange={setSelectedResourceTypes}
          resourceSearch={resourceSearch}
          onResourceSearchChange={setResourceSearch}
          resourcesDropdownOpen={resourcesDropdownOpen}
          setResourcesDropdownOpen={setResourcesDropdownOpen}
          uniqueResourceTypes={RESOURCE_TYPES}
          loadingResources={loadingResources}
          fetchProgress={fetchProgress}
          fetchError={resourceFetchError}
          organizationSelected={!!sourceOrganization && !!targetOrganization}
          searchedResources={searchedResources}
          selectedResourceIds={selectedResourceIds}
          onToggleResource={handleResourceToggle}
          onSelectAll={handleSelectAllToggle}
          dropdownRef={dropdownRef}
          dropdownButtonRef={dropdownButtonRef}
          panelRef={panelRef}
          onFetchResources={handleFetchResources}
          hasPendingTypes={hasPendingTypes}
        />

        {isFullSnapshotMode && (
          <div
            className={`mt-4 ${ui.page.sectionPadding} bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 rounded-lg`}
          >
            <div className={`flex items-center ${ui.gap.sm}`}>
              <svg
                className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <p className={`${ui.text.label} font-semibold text-blue-800 dark:text-blue-300`}>
                Full Export Mode Active
              </p>
            </div>
            <p className={`${ui.text.xs} text-blue-700 dark:text-blue-400 mt-2`}>
              All {RESOURCE_TYPES.length} resource types will be included with wildcard (*)
              selection
            </p>
          </div>
        )}
      </div>
    </>
  );

  const rightHeader = (
    <>
      <SelectedResourcesTableHeader
        typeSearch={typeSearch}
        nameSearch={nameSearch}
        onSetTypeSearchChange={setTypeSearch}
        onSetNameSearchChange={setNameSearch}
      />
    </>
  );

  const rightBody = (
    <>
      <SelectedResourcesTableBody
        filteredResourcesByType={filteredResourcesByType}
        filteredResourceTypes={filteredResourceTypes}
        typeSearch={typeSearch}
        nameSearch={nameSearch}
        isFullSnapshotMode={isFullSnapshotMode}
        resourceTypeCounts={totalCountForAllTypes}
        loadingResources={loadingResources && isPrePopulatedForm}
      />
    </>
  );

  const rightExtraContent = (
    <SelectedResourcesTableButtons
      totalSelectedCount={totalSelectedCount}
      selectedResourceIds={selectedResourceIds}
      openConfirmModal={openConfirmModal}
    />
  );

  const handleNextClick = () => {
    setHasAttemptedSubmit(true);
    if (
      validateForm(
        migrationName,
        sourceOrganization,
        targetOrganization,
        totalSelectedCount,
        isFullSnapshotMode
      )
    ) {
      handleRunMigrationClick();
    }
  };

  const buttons = (
    <div className={`flex flex-col items-end ${ui.gap.sm}`}>
      {hasAttemptedSubmit && validationError && validationError.length > 0 && (
        <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg">
          <ul className="list-disc pl-5 space-y-1">
            {validationError.map((err, idx) => (
              <li key={idx} className={`${ui.text.label} text-red-700 dark:text-red-300`}>
                {err}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex items-center space-x-3">
        <button
          className={`rounded-lg ${ui.button.wide} text-white font-semibold shadow transition
            ${
              isButtonEnabled
                ? 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 cursor-pointer'
                : 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-60'
            }`}
          onClick={handleNextClick}
        >
          Next
        </button>
      </div>
    </div>
  );

  const migrationSetupGuidanceBanner = showGuidanceBanner ? (
    <div className="rounded-xl border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 p-4">
      <div className={`flex items-start justify-between ${ui.gap.md}`}>
        <div className={`flex items-start ${ui.gap.md}`}>
          {/* Info icon */}
          <div className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-blue-500 dark:bg-blue-600 flex items-center justify-center">
            <svg
              className="w-3 h-3 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={3}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v.01M12 12v6" />
            </svg>
          </div>
          <div className={`${ui.text.label} text-blue-800 dark:text-blue-200 space-y-2`}>
            <p className={`${ui.text.body} font-semibold text-blue-900 dark:text-blue-100`}>
              Getting started with Migration Setup
            </p>
            <div className="space-y-1.5">
              <div className="flex items-start gap-2">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-blue-900 dark:text-blue-100">
                    Start with organizations —{' '}
                  </span>
                  Select a source and target organization, then choose the resources you want to
                  move.
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-blue-900 dark:text-blue-100">
                    Dependencies are handled automatically —{' '}
                  </span>
                  All dependent Genesys objects will be automatically included to ensure a complete
                  migration.
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-blue-900 dark:text-blue-100">
                    You can refine later —{' '}
                  </span>
                  You&apos;ll be able to review and remove items during the transformation step if
                  desired.
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-blue-900 dark:text-blue-100">
                    Check the Selected Resources table —{' '}
                  </span>
                  Use it on the right to confirm your selections are accurate before continuing.
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-blue-900 dark:text-blue-100">
                    Green &ldquo;Select All&rdquo; indicator —{' '}
                  </span>
                  When lit, all resources of that type will be included, even those added after the
                  migration is queued.
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Dismiss button */}
        <button
          type="button"
          onClick={() => setShowGuidanceBanner(false)}
          title="Dismiss"
          className="flex-shrink-0 text-blue-400 dark:text-blue-500 hover:text-blue-600 dark:hover:text-blue-300 transition-colors"
          aria-label="Dismiss migration setup guidance"
        >
          <svg
            className={ui.svg.sm}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  ) : null;

  return (
    <>
      {isEditMigrationPrefillLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="flex flex-col items-center gap-4 rounded-xl bg-white px-12 py-10 shadow-2xl dark:bg-[#0f1720] min-w-[320px]">
            {/* 12-spoke segmented activity-indicator spinner */}
            <svg
              viewBox="0 0 48 48"
              className="w-16 h-16"
              aria-hidden="true"
            >
              {Array.from({ length: 12 }).map((_, i) => (
                <rect
                  key={i}
                  x="22"
                  y="4"
                  width="4"
                  height="10"
                  rx="2"
                  fill="currentColor"
                  className="text-blue-500"
                  opacity={((i + 1) / 12).toFixed(2)}
                  transform={`rotate(${i * 30} 24 24)`}
                  style={{
                    animation: 'prefill-spoke-fade 1s linear infinite',
                    animationDelay: `${-(12 - i) * (1 / 12)}s`
                  }}
                />
              ))}
            </svg>
            <div className="flex flex-col items-center gap-1 text-center">
              <p className={`${ui.text.subTitle} font-semibold text-gray-900 dark:text-gray-100`}>
                Restoring your migration
              </p>
              <p className={`${ui.text.body} text-gray-500 dark:text-gray-400`}>
                Filling in your previous settings from the failed migration and fetching resources from the source organization.
              </p>
            </div>
            <ul className={`${ui.text.xs} w-full space-y-1 text-gray-500 dark:text-gray-400`}>
              <li className="flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                Restoring migration name &amp; organizations
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                Loading resource types from the failed migration
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                Fetching &amp; re-selecting your resources — please wait
              </li>
            </ul>
          </div>
        </div>
      )}
      <SetupLayout
        title="Migration Details"
        titleAddon={
          <button
            type="button"
            onClick={() => setShowFAQs(true)}
            className="self-start mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-semibold shadow-sm hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:border-blue-400 dark:hover:border-blue-600 hover:shadow-md transition-all duration-150 group"
            aria-label="View RepliCloud FAQs"
          >
            <svg
              className="w-3.5 h-3.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-150"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z"
              />
            </svg>
            <span className="font-normal text-blue-500 dark:text-blue-400">Need help?</span>
            <span>FAQ</span>
          </button>
        }
        topBanner={migrationSetupGuidanceBanner}
        leftContent={leftContent}
        rightHeader={rightHeader}
        rightBody={rightBody}
        rightContentWrapperClass={tableScrollClass}
        rightExtraContent={rightExtraContent}
        selectedResourceTitle={
          isFullSnapshotMode ? 'Full Export (All Resources)' : selectedResourceTitle
        }
        selectedResourceCount={isFullSnapshotMode ? RESOURCE_TYPES.length : totalSelectedCount}
        buttonSection={buttons}
      />
      <Modal
        isOpen={modalState.variant === 'delete' ? confirmModalOpen : modalState.isOpen}
        variant={modalState.variant}
        title={
          modalState.variant === 'delete'
            ? confirmScope === 'all'
              ? 'Clear ALL Resource Type Selections'
              : 'Clear CURRENT Resource Type Selections'
            : 'Confirm Migration'
        }
        onClose={
          modalState.variant === 'delete'
            ? () => {
                logger.info('Modal closed', { type: 'delete', action: 'cancelled' });
                setConfirmModalOpen(false);
              }
            : () => {
                logger.info('Modal closed', { type: 'migration', action: 'cancelled' });
                handleModalClose();
              }
        }
        onConfirm={
          modalState.variant === 'delete'
            ? () => {
                if (confirmScope === 'all') {
                  handleResetSelections('all');
                  setIsFullSnapshotMode(false);
                  setSelectedResourceTypes([]);
                } else {
                  // Only clear the types the user just interacted with (the current selectedResourceTypes)
                  handleResetSelections('current');
                  setSelectedResourceTypes((prev) =>
                    prev.filter((type) => !selectedResourceTypes.includes(type))
                  );
                }
                setConfirmModalOpen(false);
              }
            : handleRunConfirm
        }
        confirmLabel={modalState.variant === 'delete' ? 'Yes, clear' : 'Start Migration'}
        cancelLabel="Cancel"
        showConfirmButton={true}
        showIcon={true}
        confirmLoading={confirmLoading}
        confirmLoadingLabel="Starting migration..."
        showCloseButton
        setError={setError}
        error={error}
      >
        {
          ((): React.ReactNode => {
            if (modalState.variant === 'delete') {
              return (
                <>
                  <p className="text-sm text-center text-gray-700 dark:text-gray-300">
                    {confirmScope === 'all'
                      ? 'This will remove all saved selections across every resource type. This action cannot be undone.'
                      : 'This will clear selections only for the currently selected resource type.'}
                  </p>
                </>
              );
            }

            if (modalState.variant === 'runMigration') {
              return (
                <div className="w-full max-w-2xl space-y-3">
                  {/* Summary Card */}
                  <div className="space-y-3">
                    {/* Names - Stacked */}
                    <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                        <svg
                          className="w-4 h-4 text-orange-600 dark:text-orange-400"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                          />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Migration Name
                        </div>
                        <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 break-words">
                          {migrationName || '[Name]'}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                        <svg
                          className="w-4 h-4 text-purple-600 dark:text-purple-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Export Name
                        </div>
                        <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 break-words">
                          {snapshotName || '[Name]'}
                        </div>
                      </div>
                    </div>

                    {/* Source Org */}
                    <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                        <svg
                          className="w-4 h-4 text-teal-600 dark:text-teal-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Source Org
                        </div>
                        <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 break-words">
                          {sourceOrganization ? (
                            <>
                              {sourceOrganization} -{' '}
                              {(() => {
                                const org = findOrganization(sourceOrganization);
                                return formatRegion(org?.region);
                              })()}
                            </>
                          ) : (
                            '[Org]'
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Target Org */}
                    <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                        <svg
                          className="w-4 h-4 text-indigo-600 dark:text-indigo-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Target Org
                        </div>
                        <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 break-words">
                          {targetOrganization ? (
                            <>
                              {targetOrganization} -{' '}
                              {(() => {
                                const org = findOrganization(targetOrganization);
                                return formatRegion(org?.region);
                              })()}
                            </>
                          ) : (
                            '[Org]'
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Resources */}
                    <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                      <div className="p-2 bg-blue-100 dark:bg-blue-800/50 rounded-lg">
                        <svg
                          className="w-4 h-4 text-blue-600 dark:text-blue-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                          {isFullSnapshotMode ? 'Resource Types' : 'Resources'}
                        </div>
                        <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
                          {isFullSnapshotMode ? RESOURCE_TYPES.length : allSelectedResources.length}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Resources by Type - Accordion */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <SelectedResourcesTableHeader
                      typeSearch={typeSearch}
                      nameSearch={nameSearch}
                      onSetTypeSearchChange={setTypeSearch}
                      onSetNameSearchChange={setNameSearch}
                    />
                    <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto">
                      <SelectedResourcesTableBody
                        filteredResourcesByType={filteredResourcesByType}
                        filteredResourceTypes={filteredResourceTypes}
                        typeSearch={typeSearch}
                        nameSearch={nameSearch}
                        isFullSnapshotMode={isFullSnapshotMode}
                        resourceTypeCounts={totalCountForAllTypes}
                      />
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })() as React.ReactNode
        }
      </Modal>
      <RepliCloudFAQsModal
        isOpen={showFAQs}
        onClose={() => setShowFAQs(false)}
        onBack={() => setShowFAQs(false)}
      />
    </>
  );
};

export default MigrationSetup;
